<template>
  <template v-if="props.rail">
    <v-menu
      v-model="railMenuOpen"
      :close-on-content-click="true"
      location="end"
    >
      <template #activator="{ props: menuProps }">
        <v-btn
          v-bind="menuProps"
          icon
          size="small"
          variant="tonal"
          color="secondary"
          :aria-label="railAriaLabel"
          aria-haspopup="menu"
          :aria-expanded="railMenuOpen ? 'true' : 'false'"
        >
          <v-icon icon="mdi-notebook-outline" />
          <s-tooltip activator="parent" location="end">{{ railTooltip }}</s-tooltip>
        </v-btn>
      </template>
      <v-card min-width="260" max-height="360" class="overflow-y-auto">
        <v-list density="compact" nav>
          <v-list-item
            to="/leakprofiles/new/"
            prepend-icon="mdi-plus"
            title="Create Leak Profile"
            @click="railMenuOpen = false"
          />
          <v-divider class="my-1" />
          <template v-if="allItems === undefined">
            <div class="d-flex justify-center pa-4">
              <v-progress-circular indeterminate size="24" />
            </div>
          </template>
          <template v-else-if="allItems.length === 0">
            <v-list-item
              v-if="!fetchItems.hasNextPage.value"
              title="No leak profiles"
              disabled
            />
            <div v-else class="d-flex justify-center pa-4">
              <page-loader :items="fetchItems" />
            </div>
          </template>
          <template v-else>
            <v-list-item
              v-for="item in allItems"
              :key="item.id"
              :title="item.name"
              :active="item.id === leakProfile?.id"
              @click="selectRailItem(item)"
            />
            <page-loader v-if="allItems.length > 0" :items="fetchItems" />
          </template>
        </v-list>
      </v-card>
    </v-menu>
  </template>
  <s-autocomplete
    v-else
    v-model="leakProfile"
    :items="allItems"
    item-value="id"
    item-title="name"
    :return-object="true"
    :clearable="false"
    :hide-no-data="false"
    spellcheck="false"
    v-bind="$attrs"
  >
    <template #append-item v-if="(allItems?.length ?? 0) > 0">
      <page-loader :items="fetchItems" />
    </template>
    <template #no-data v-if="(allItems?.length ?? 0) === 0 && fetchItems.hasNextPage.value">
      <page-loader :items="fetchItems" />
    </template>
  </s-autocomplete>
</template>

<script setup lang="ts">
const leakProfile = defineModel<LeakProfileShort|null>();

const props = withDefaults(
  defineProps<{
    rail?: boolean;
  }>(),
  { rail: false },
);

const railMenuOpen = ref(false);

const fetcherOptions = {
  baseURL: '/api/v1/leakprofiles/',
  query: {
    ordering: 'name,created',
  }
}
const fetchItems = useSearchableCursorPaginationFetcher<LeakProfileShort>(fetcherOptions);

useLazyAsyncData(async () => {
  if (!leakProfile.value && toValue(fetchItems.data).length === 0) {
    await fetchItems.fetchNextPage();
  }
});

const allItems = computed(() => {
  if (fetchItems.data.value.length === 0 && fetchItems.pending.value) {
    // Initial fetch of leakprofiles is pending
    return undefined;
  }
  const out = [...toValue(fetchItems.data)];
  if (leakProfile.value && !out.some(lp => lp.id === leakProfile.value?.id)) {
    out.unshift(leakProfile.value);
  }
  return out;
});

const railTooltip = computed(() => {
  const name = leakProfile.value?.name;
  return name
    ? `Leak profile: ${name} (choose another)`
    : 'Choose leak profile';
});

const railAriaLabel = computed(() =>
  leakProfile.value?.name
    ? `Choose leak profile, current ${leakProfile.value.name}. Opens a menu.`
    : 'Choose leak profile. Opens a menu.',
);

function selectRailItem(item: LeakProfileShort) {
  leakProfile.value = item;
  railMenuOpen.value = false;
}

watch(leakProfile, () => {
  const out = [...toValue(fetchItems.data)]
  if (leakProfile.value && !out.some(lp => lp.id === leakProfile.value?.id)) {
    // A new leak profile was created probably. Resetting fetcher so that it doesn't disappear later from the selection.
    fetchItems.reset(fetcherOptions);
  }
});

watch(
  () => leakProfile.value?.name,
  (name) => {
    // LeakProfile name was updated. Update the name in the Leak Profile Selection list
    const out = [...toValue(fetchItems.data)]
    const lp = out.find(lp => lp.id === leakProfile.value?.id && lp.name !== name);
    if (lp && name) {
      lp.name = name;
    }
  }
)

defineExpose({
  leakprofiles: allItems,
})
</script>
